import { Button, Space } from 'antd';
import { ButtonProps } from 'antd/lib/button';
import clx from 'classnames';
import React, { useRef, useState } from 'react';
import useDimensions from 'react-cool-dimensions';
import styled from 'styled-components';
import { useHover } from 'usehooks-ts';

const HoverContent = styled(Space)`
  opacity: 0;
  transition-delay: 0.1s;
  transition: opacity 0.2s;
  border-radius: 8px; // 8px;
  background-color: #fff;
  box-shadow: 0px 0px 5px 5px #fff;
  .is-container & {
    background-color: ${props => props.theme.colorBorderSecondary};
    box-shadow: none;
    padding: 8px;
    margin-right: -8px;
  }

  /* pointer-events: none; */
`;
const ExpandButtonWrapper = styled(Space)`
  opacity: 0;
  transition-delay: 0.1s;
  transition: opacity 0.2s;

  position: absolute;
  bottom: 8px;
  left: 8px;
  border-radius: 8px; // 8px;
  background-color: #fff;
  box-shadow: 0px 0px 5px 5px #fff;
  &.is-container {
    bottom: unset;
    top: 8px;
  }

  &.show-button {
    box-shadow: 0px 0px 5px 5px transparent;
    opacity: 1;
    .ant-btn {
      transition: all 0.2s;
      background-color: ${props => props.theme.colorWarning};
      border-color: ${props => props.theme.colorWarning};
      color: #fff;
      &:hover {
        background-color: ${props => props.theme.colorWarningHover};
        border-color: ${props => props.theme.colorWarningHover};
      }
    }
  }
`;

const HoverWrapper = styled.div`
  // posizioniamo il div sopra il contenuto
  z-index: 5;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  overflow: hidden;

  // inseriamo un margine negativo e un padding per contornare l'elemento sottostante
  margin: -8px;
  padding: 8px;
  border: 2px dashed transparent;
  border-radius: 8px;

  // fix altezza Input con testo di aiuto
  margin-bottom: 0px;

  justify-content: flex-end;
  align-items: flex-end;
  transition: all 0.3s;

  // per mostrare l'icona centrata
  /* &.is-drag-overlay {
    justify-content: center;
  } */
  &.is-container {
    align-items: flex-start;
    padding-top: 0px;
    &:hover {
      ${HoverContent} {
        opacity: 1;
        background-color: ${props => props.theme.colorPrimaryBg};
      }
    }
  }
  &:hover {
    border-color: ${props => props.theme.colorPrimary};

    cursor: grab;
    &.is-drag-overlay {
      cursor: grabbing;
    }
    ${HoverContent}, ${ExpandButtonWrapper} {
      opacity: 1;
    }
    &:active {
    }
    &.is-drag-overlay,
    &.is-over-me,
    &.is-moving {
      border-color: transparent;
    }
  }
`;

export interface HoverActionsContainerProps
  extends React.HTMLAttributes<HTMLDivElement> {
  isDragOverlay?: boolean;
  isOverMe?: boolean;
  isMoving?: boolean;
  isContainer?: boolean;
  // per mantenere lhover per il pulsante elimina
  isPopConfirmOpen?: boolean;
  // per gestire l'espandi del widget
  isCompact?: boolean;
  compactButtonProps?: ButtonProps;
}

export function HoverActionsContainer(props: HoverActionsContainerProps) {
  const {
    isDragOverlay,
    children,
    isCompact,
    compactButtonProps,
    ...otherProps
  } = props;
  const [compactButtonVisible, setCompactButtonVisible] = useState(false);
  const heightTarget = props.isContainer ? 200 : 100;

  const { observe, unobserve, width, height, entry } = useDimensions({
    onResize: ({ observe, unobserve, width, height, entry }) => {
      if (height > heightTarget) {
        setCompactButtonVisible(true);
      } else {
        setCompactButtonVisible(isCompact ?? false);
      }
      unobserve();
      observe();
    },
  });

  // Non possiamo utilizzare solo i CSS per l'hover perché il rendering
  // stesso (lato React) dei pulsanti è piuttosto lento, e viene triggerato
  // durante il D&D. Inoltre non è sufficiente `isHover` ma dobbiamo controllare
  // che non si stia draggando un widget (`!isMoving`)
  const hoverRef = useRef<HTMLDivElement | null>(null);
  const isHover = useHover(hoverRef);
  const isHoverOrPopConfirmOpen = isHover || props.isPopConfirmOpen;

  return (
    <HoverWrapper
      ref={ref => {
        observe(ref);
        hoverRef.current = ref;
      }}
      className={clx({
        'is-drag-overlay': isDragOverlay,
        'is-over-me': props.isOverMe,
        'is-moving': props.isMoving,
        'is-container': props.isContainer,
      })}
      {...otherProps}
    >
      {!isDragOverlay && isHoverOrPopConfirmOpen && !props.isMoving && (
        <HoverContent>{children}</HoverContent>
      )}
      {!isDragOverlay && !props.isMoving && compactButtonVisible && (
        <ExpandButtonWrapper
          className={clx({
            'show-button': isCompact,
            'is-container': props.isContainer,
          })}
        >
          <Button
            size="small"
            children="compatta"
            {...compactButtonProps}
            className={clx({
              'show-button-compact': isCompact,
            })}
          />
        </ExpandButtonWrapper>
      )}
    </HoverWrapper>
  );
}
